import apiRequest from "../../utils/apiRequest";

/**
 * Services HTTP du domaine Diseases (Phase 2.F).
 *
 * <p>Aligné sur le contrat back :
 * <ul>
 *   <li>POST /diseases → 201 + Long (id).</li>
 *   <li>PUT /diseases/{id} → Long (id).</li>
 *   <li>DELETE /diseases/{id} → void.</li>
 *   <li>GET /diseases/{id} → DiseasesResponse.</li>
 *   <li>GET /diseases?search&page&size → Map paginée
 *       (PageMapper.toMap : content/totalElements/...). Remplace
 *       l'ancien POST /diseases/get-filter supprimé en Phase 14.</li>
 * </ul>
 *
 * <p>Toutes les fonctions retournent l'enveloppe uniforme apiRequest :
 * {@code { ok, status, data, message?, error? }}.
 */

export const createDiseases = async (payload) => {
  return apiRequest.post("/diseases", payload);
};

export const updateDisease = async ({ data, id } = {}) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.put(`/diseases/${id}`, data);
};

export const deleteDiseases = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.del(`/diseases/${id}`);
};

export const getDiseasesById = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/diseases/${id}`);
};

/**
 * Catalogue paginé des maladies — remplace l'ancien
 * {@code POST /diseases/get-filter}.
 *
 * @param {object} [params={}]
 * @param {string} [params.search='']
 * @param {number} [params.page=0]
 * @param {number} [params.size=20]
 * @returns enveloppe avec {@code data} = {@code {content, totalElements, ...}}.
 */
export const getDiseases = async ({ search = "", page = 0, size = 20 } = {}) => {
  const qs = new URLSearchParams({ search, page: String(page), size: String(size) }).toString();
  return apiRequest.get(`/diseases?${qs}`);
};

/**
 * @deprecated alias rétro-compatible vers {@link getDiseases}.
 *
 * <p>L'ancien {@code POST /diseases/get-filter} acceptait un objet de filtre
 * arbitraire et renvoyait un tableau plat. Pour préserver l'API des
 * consommateurs (saga, hook {@code useGetDiseasesByFilter}), cet alias
 * <strong>aplatit</strong> la réponse paginée : il extrait
 * {@code page.content} et le retourne dans {@code data}, comme avant.
 */
export const getDiseasesByFilter = async (payload = {}) => {
  const search =
    payload?.search ?? payload?.label ?? payload?.name ?? payload?.q ?? "";
  const result = await getDiseases({ search });
  if (!result?.ok) return result;
  return { ...result, data: result.data?.content ?? [] };
};

/**
 * Lookup léger {id, label} search-as-you-type pour les selects — adapté
 * au catalogue CIM-10 (~14k entrées) où charger toute la table serait
 * prohibitif.
 *
 * <p>Règles serveur :
 * <ul>
 *   <li>{@code q} doit faire au moins 2 caractères ; sinon le serveur renvoie
 *       {@code []} (200, pas une 400).</li>
 *   <li>{@code limit} par défaut 20, plafonné serveur à 50.</li>
 *   <li>recherche par préfixe insensible casse/accents.</li>
 * </ul>
 *
 * @param {object} [params={}]
 * @param {string} [params.q='']
 * @param {number} [params.limit=20]
 * @returns enveloppe avec {@code data} = {@code LookupItem[]}.
 */
export const getDiseasesLookup = async ({ q = "", limit = 20 } = {}) => {
  const qs = new URLSearchParams({ q, limit: String(limit) }).toString();
  return apiRequest.get(`/diseases/lookup?${qs}`);
};

/**
 * Lookup d'une maladie par id — utilisé pour précharger le label
 * affiché dans un select en édition.
 *
 * @returns enveloppe avec {@code data} = {@code LookupItem} ou 404.
 */
export const getDiseasesLookupById = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/diseases/lookup/${id}`);
};
